import Axios from "axios";
import axios from "./axios";

/**
 * Media API - enterprise object storage via the backend (MinIO).
 *
 * Replaces the previous direct-to-Firebase upload flow. The browser asks the
 * backend for a short-lived presigned PUT URL, uploads the bytes straight to
 * MinIO (no proxying through the API), then receives a presigned GET URL that
 * is stored in the backend as `scan_id` (same contract the app already uses).
 *
 * Backend endpoints (cdnktapi):
 *   POST /media/presign-upload   -> { upload_url, headers, download_url_after, key, ... }
 *   POST /media/presign-download -> { url }
 */

export interface PresignUploadResponse {
  key: string;
  bucket: string;
  upload_url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_in: number;
  download_url_after: string;
}

export interface UploadResult {
  /** Presigned GET URL - store this as `scan_id`. */
  url: string;
  /** Object key inside the bucket (useful for later deletion). */
  key: string;
}

/** Maps a logical area to the storage directory used by the backend. */
export type StorageDirectory =
  | "vehicle/picture"
  | "strongbox"
  | "license"
  | "user/identity"
  | "user/avatar"
  | "sinistre"
  | "sign"
  | "company"
  /**
   * PERMANENT-URL namespace.
   *
   * Only the "public/" prefix carries an anonymous-read policy on the bucket,
   * so `download_url_after` comes back UNSIGNED there — short, cacheable, and
   * it never expires. Everywhere else the backend hands back a presigned GET
   * with X-Amz-Expires=604800, which is fine for a one-off download but is a
   * time bomb if you persist it (see `refreshDownloadUrl` below).
   *
   * Use these for anything whose URL gets written into a database row and
   * displayed weeks later.
   */
  | "public/company"
  | "misc";

/**
 * Request a presigned upload descriptor from the backend.
 */
async function requestPresignUpload(params: {
  mimeType: string;
  directory?: StorageDirectory;
  filename?: string;
}): Promise<PresignUploadResponse> {
  const { data } = await axios.post<PresignUploadResponse>(
    "/media/presign-upload",
    {
      mime_type: params.mimeType,
      directory: params.directory ?? "misc",
      filename: params.filename,
    }
  );
  return data;
}

/**
 * Upload a File/Blob to MinIO via a presigned PUT URL.
 *
 * The PUT goes directly to MinIO (storage.codenekt.com), NOT through the API,
 * so large files never buffer in the backend. We use a bare Axios instance for
 * the PUT so the app auth interceptors/headers are not attached to MinIO.
 *
 * @returns The presigned download URL (store as `scan_id`) and the object key.
 */
export async function uploadToStorage(
  file: File | Blob,
  options?: { directory?: StorageDirectory; filename?: string }
): Promise<UploadResult> {
  const mimeType = (file as File).type || "application/octet-stream";
  const filename = options?.filename ?? (file as File).name ?? undefined;

  const presigned = await requestPresignUpload({
    mimeType,
    directory: options?.directory,
    filename,
  });

  await Axios.put(presigned.upload_url, file, {
    headers: {
      ...presigned.headers,
      "Content-Type": mimeType,
    },
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });

  return { url: presigned.download_url_after, key: presigned.key };
}

/**
 * Ask the backend for a fresh presigned GET URL for an existing object key.
 * Presigned URLs expire, so views that display stored files long after upload
 * should refresh the URL via this call rather than persisting a stale link.
 */
export async function refreshDownloadUrl(
  key: string,
  expiresIn = 900
): Promise<string> {
  const { data } = await axios.post<{ key: string; url: string }>(
    "/media/presign-download",
    { key, expires_in: expiresIn }
  );
  return data.url;
}

/**
 * Delete one or more objects from storage.
 */
export async function deleteFromStorage(
  keys: string | string[]
): Promise<void> {
  const payload = Array.isArray(keys) ? { keys } : { key: keys };
  await axios.delete("/media", { data: payload });
}
// ---------------------------------------------------------------------------
// Backwards-compatible wrappers (previously exported by utils/firebaseConfig,
// now MinIO-backed). Kept so existing call-sites keep the same signatures.
// ---------------------------------------------------------------------------

/** Fetch a blob from an object URL / data URI. */
export function uriToBlob(uri: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => resolve(xhr.response);
    xhr.onerror = () => reject(new Error("uriToBlob failed"));
    xhr.responseType = "blob";
    xhr.open("GET", uri, true);
    xhr.send(null);
  });
}

/**
 * Upload a single File/Blob and return its presigned download URL.
 * Old signature: uploadFileMultiple(file, directory) -> url string
 */
export async function uploadFileMultiple(
  file: File | Blob,
  directory: StorageDirectory = "misc"
): Promise<string> {
  const { url } = await uploadToStorage(file, { directory });
  return url;
}

/**
 * Upload an image given an object URL / data URI and return its download URL.
 * Old signature: uploadImage(uri, contentType?, directory?) -> url string
 */
export async function uploadImage(
  image: string | File | Blob,
  contentType = "image/jpeg",
  directory: StorageDirectory = "misc"
): Promise<string> {
  const blob: Blob =
    typeof image === "string"
      ? await uriToBlob(image)
      : (image as Blob);
  const typedBlob =
    blob.type && blob.type !== "" ? blob : blob.slice(0, blob.size, contentType);
  const { url } = await uploadToStorage(typedBlob, { directory });
  return url;
}

/**
 * Public URL for a vehicle-model stock picture stored in MinIO.
 * Assets live under public/vehicle-models/{modelId}.jpg (anonymous download),
 * seeded by the backend command: php artisan storage:seed-vehicle-model-assets
 */
export function getModelPictureUrl(modelId: number): string {
  const base =
    process.env.REACT_APP_STORAGE_PUBLIC_URL ||
    "https://storage.codenekt.com/codenekt";
  return `${base}/public/vehicle-models/${modelId}.jpg`;
}
